/**
 * TermsGatePage — one-screen terms acceptance for users who signed in without it
 * (social login) or whose acceptance predates the currently effective terms.
 *
 * On success the user is re-fetched into the auth store: the store is not persisted
 * and never refreshes itself, so a stale flag would bounce the user back here.
 */

import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useAuthStore } from "@/stores/authStore"
import { acceptTerms } from "@/services/api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ConsentLabel } from "@/components/low/ConsentLabel"

/** Keep navigation inside the app: a protocol-relative value would leave it. */
function safeReturnUrl(raw: string | null): string {
  if (raw === null || !raw.startsWith("/") || raw.startsWith("//")) return "/"
  return raw
}

export function TermsGatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnUrl = safeReturnUrl(searchParams.get("returnUrl"))
  const setUser = useAuthStore((s) => s.setUser)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleAccept = async () => {
    setIsSubmitting(true)
    try {
      const user = await acceptTerms()
      setUser(user)
      navigate(returnUrl, { replace: true })
    } catch (err) {
      const message = (err as { message?: string }).message ?? t("terms.acceptFailed")
      toast.error(message)
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("terms.gateTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">{t("terms.gateDescription")}</p>
          <p className="text-sm">
            <ConsentLabel />
          </p>
          <Button
            data-testid="accept-terms-button"
            onClick={handleAccept}
            disabled={isSubmitting}
            className="w-full min-h-[44px]"
          >
            {isSubmitting ? t("terms.accepting") : t("terms.accept")}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
